import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../../context/AuthContext';
import './OnboardingModal.css';

const FEATURES = [
  {
    title: 'Plan curvy routes',
    desc: 'Drop points or search places — snap to roads with distance, elevation & twistiness.',
    icon: <path d="M20 10c0 6-8 12-8 12S4 16 4 10a8 8 0 0 1 16 0Z M12 7v0" />,
  },
  {
    title: 'Generate rides',
    desc: 'Auto-suggest destinations at any distance, then route back home in one tap.',
    icon: <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8 M3 3v5h5" />,
  },
  {
    title: 'Discover and publish interesting routes',
    desc: 'Browse and load rides shared by other riders — filter to what’s near you.',
    icon: <path d="M9 20l-5.5 2.5v-15L9 5m0 15 6-3m-6 3v-15m6 12 5.5 2.5v-15L15 4m0 13V4m0 0L9 7" />,
  },
  {
    title: 'Join group tours',
    desc: 'Find and register for guided motorcycle tours — or host your own.',
    icon: <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8 M23 21v-2a4 4 0 0 0-3-3.87 M16 3.13a4 4 0 0 1 0 7.75" />,
  },
  {
    title: 'Download GPX files',
    desc: 'Export any route as a GPX file to load on your GPS or navigation app.',
    icon: <path d="M12 3v12 M7 10l5 5 5-5 M5 21h14" />,
  },
];

export function OnboardingModal({ onClose }) {
  const { signIn } = useAuth();
  const overlayRef = useRef(null);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => { document.body.style.overflow = prev; document.removeEventListener('keydown', onKey); };
  }, [onClose]);

  return createPortal(
    <div className="ob-overlay" ref={overlayRef} onClick={(e) => e.target === overlayRef.current && onClose()}>
      <div className="ob-modal" role="dialog" aria-modal="true" aria-labelledby="ob-title">
        <button className="ob-close" onClick={onClose} aria-label="Close">
          <svg width="13" height="13" viewBox="0 0 13 13" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
            <line x1="1" y1="1" x2="12" y2="12"/><line x1="12" y1="1" x2="1" y2="12"/>
          </svg>
        </button>

        <div className="ob-hero">
          <img src="/motoroam_logo.png" alt="" className="ob-hero__mark" />
          <h2 className="ob-hero__title" id="ob-title">Welcome to Motoroam</h2>
          <p className="ob-hero__sub">Free route planning &amp; tours, built for riders.</p>
        </div>

        <div className="ob-features">
          {FEATURES.map(f => (
            <div key={f.title} className="ob-feature">
              <span className="ob-feature__icon">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">{f.icon}</svg>
              </span>
              <div className="ob-feature__text">
                <span className="ob-feature__title">{f.title}</span>
                <span className="ob-feature__desc">{f.desc}</span>
              </div>
            </div>
          ))}
        </div>

        <div className="ob-actions">
          <button className="ob-google" onClick={signIn}>
            <svg width="16" height="16" viewBox="0 0 24 24" style={{ flexShrink: 0 }}>
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/>
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
            </svg>
            Continue with Google
          </button>
          <button className="ob-explore" onClick={onClose}>Explore the map first</button>
        </div>

        <p className="ob-foot">Free to use · sign in only to save, publish &amp; join tours.</p>
      </div>
    </div>,
    document.body
  );
}
